import { HeroEridMockup } from "@/components/landing/HeroEridMockup";
import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Shield, Zap, Bot, ArrowRight, CheckCircle2, Star, MessageSquareQuote, MessageSquare, Loader2, Send, BookOpen, Menu, Home, FileText, Info, Mail, CreditCard, LogIn, Target, Briefcase, GitCompare, ListChecks, ImageIcon, ChevronDown, Calculator, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";

import { TactileButton } from "@/components/TactileButton";
import { lazy, Suspense } from "react";
const PenaltyRiskCalculator = lazy(() => import("@/components/PenaltyRiskCalculator").then(m => ({ default: m.PenaltyRiskCalculator })));
const AiLiveActivityLazy = lazy(() => import("@/components/landing/AiLiveActivity").then(m => ({ default: m.AiLiveActivity })));
const LandingMarkSandbox = lazy(() => import("@/components/landing/LandingMarkSandbox").then(m => ({ default: m.LandingMarkSandbox })));
import { AiLiveActivitySkeleton } from "@/components/landing/AiLiveActivity";
import { AliveIcon, type AliveAnimation } from "@/components/ui/AliveIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
// Accordion now used inside FaqExtended
import { SEOHead } from "@/components/SEOHead";
const getSupabase = () => import("@/integrations/supabase/client").then(m => m.supabase);
import { toast } from "sonner";

import { reachGoal } from "@/lib/metrika";
import { type Persona } from "@/components/landing/HeroSandbox";
const HeroSandbox = lazy(() => import("@/components/landing/HeroSandbox").then((m) => ({ default: m.HeroSandbox })));
import { HeroAiTextScanner } from "@/components/landing/HeroAiTextScanner";
import { SocialProofMetricsBand } from "@/components/landing/SocialProofMetricsBand";
import { BookDemoFab } from "@/components/landing/BookDemoFab";
import { MouseFollowRefraction } from "@/components/MouseFollowRefraction";
import { RoleCards } from "@/components/landing/RoleCards";
import { TrustBar } from "@/components/landing/TrustBar";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { AudienceTabs } from "@/components/landing/AudienceTabs";
import { StickyScrollCTA } from "@/components/landing/StickyScrollCTA";
import { OrdPartnersStrip } from "@/components/landing/OrdPartnersStrip";
import { MaxChannelButton } from "@/components/landing/MaxChannelButton";
import eridonLogo from "@/assets/eridon-logo.png";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroPenaltyCalculator } from "@/components/landing/HeroPenaltyCalculator";
import { StatsCounter } from "@/components/landing/StatsCounter";
import { ComparisonTable } from "@/components/landing/ComparisonTable";
import { GuaranteeBlock } from "@/components/landing/GuaranteeBlock";
import { CasesGrid } from "@/components/landing/CasesGrid";
import { FaqExtended } from "@/components/landing/FaqExtended";
import { FAQ_EXTENDED } from "@/data/faqExtended";
import { MobileEridBar } from "@/components/landing/MobileEridBar";
import { UrgentOfferBar } from "@/components/landing/UrgentOfferBar";
import { RelatedPagesGrid } from "@/components/landing/RelatedPagesGrid";

const RiskQuiz = lazy(() => import("@/components/landing/RiskQuiz").then(m => ({ default: m.RiskQuiz })));
import { PROMO_HEADLINE } from "@/lib/promo";
import { DemoRequestDialog } from "@/components/landing/DemoRequestDialog";

const highlights: { icon: typeof Shield; text: string; animation: AliveAnimation }[] = [
  { icon: Shield, text: "Полное соответствие 347-ФЗ и 168-ФЗ", animation: "pulse" },
  { icon: Zap, text: "erid-токены за секунды, а не дни", animation: "bounce" },
  { icon: Bot, text: "AI-агент подаёт отчёты автоматически", animation: "glow" },
];

// FAQ moved to FaqExtended component

const testimonials = [
  { name: "Анна Ковалёва", role: "Маркетинг-директор, «СберМаркет»", text: "ERIDON сэкономил нам 200+ часов в месяц на маркировке. Раньше команда из 3 человек занималась только erid-токенами.", rating: 5 },
  { name: "Дмитрий Петров", role: "CEO, Digital-агентство «Пиксель»", text: "После подключения ERIDON — ноль штрафов за 8 месяцев. До этого получали предупреждения от ФАС каждый квартал.", rating: 5 },
  { name: "Елена Смирнова", role: "Юрист, «Яндекс Реклама»", text: "AI-сканер по ФЗ-168 — это находка. Он ловит иностранные слова, которые мы пропускали при ручной проверке.", rating: 5 },
  { name: "Максим Волков", role: "Владелец интернет-магазина", text: "Калькулятор рисков открыл глаза — потенциальный штраф был 4.5 млн ₽. Подписка на ERIDON окупилась за первый день.", rating: 4 },
];



export default function Landing() {
  const [persona, setPersona] = useState<Persona>("blogger");
  const [demoOpen, setDemoOpen] = useState(false);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleAnchorNav = useCallback((id: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 220);
  }, []);

  const isActiveRoute = (path: string) => location.pathname === path;

  // Contact form state
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);

  const handleContactSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactName.trim() || !contactEmail.trim() || !contactMessage.trim()) {
      toast.error("Заполните все поля");
      return;
    }
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail.trim())) {
      toast.error("Укажите корректный email");
      return;
    }
    // Client-side rate limit: max 3 submissions per hour
    const RL_KEY = "contact_form_timestamps";
    const now = Date.now();
    const stored: number[] = JSON.parse(localStorage.getItem(RL_KEY) || "[]");
    const recent = stored.filter((t) => now - t < 3600_000);
    if (recent.length >= 3) {
      toast.error("Слишком много сообщений. Попробуйте позже.");
      return;
    }
    setContactSending(true);
    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke("submit-contact", {
        body: {
          name: contactName.trim(),
          email: contactEmail.trim(),
          message: contactMessage.trim(),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      // Track submission timestamp
      recent.push(now);
      localStorage.setItem(RL_KEY, JSON.stringify(recent));


      toast.success("Сообщение отправлено! Мы свяжемся с вами в ближайшее время.");
      setContactName("");
      setContactEmail("");
      setContactMessage("");
    } catch (err: any) {
      toast.error(err?.message || "Ошибка отправки");
    } finally {
      setContactSending(false);
    }
  }, [contactName, contactEmail, contactMessage]);

  return (
    <div className="relative flex min-h-screen flex-col overflow-x-hidden bg-background">
      <SEOHead
        title="ERIDON AI — AI Compliance Infrastructure для рекламы"
        description="AI-инфраструктура для маркировки рекламы: ERID за секунды, AI-проверка креативов, ФЗ-347/168, отчёты в ЕРИР. Для Telegram, агентств и enterprise-команд."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "ERIDON",
            url: "https://eridonai.ru",
            logo: "https://eridonai.ru/favicon.png",
            description: "ИИ-автоматизация маркировки интернет-рекламы. Соответствие ФЗ-347, ФЗ-168 и ФЗ-152.",
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+7-901-578-22-13",
              contactType: "customer service",
              availableLanguage: "Russian",
            },
            sameAs: [
              "https://vk.com/eridonai",
              "https://t.me/eridonai",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "ERIDON",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            url: "https://eridonai.ru",
            description: "ИИ-агент для автоматической маркировки рекламы по ФЗ-347, получения erid-токенов и подачи отчётов в ЕРИР.",
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "RUB",
              lowPrice: "0",
              highPrice: "4990",
              offerCount: "3",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_EXTENDED.flatMap((cat) =>
              cat.items.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              }))
            ),
          },
        ]}
      />
      {/* Sticky scroll CTA */}
      <StickyScrollCTA />
      {/* Mouse-follow refraction removed — corporate B2B aesthetic */}
      {/* Static mesh gradients */}
      <div className="pointer-events-none absolute -right-60 -top-60 h-[700px] w-[700px] rounded-full opacity-20 blur-[140px]" style={{ background: "radial-gradient(circle, hsl(243 75% 59% / 0.7) 0%, hsl(174 60% 50% / 0.3) 60%, transparent 100%)" }} />
      <div className="pointer-events-none absolute -left-40 bottom-20 h-[500px] w-[500px] rounded-full opacity-15 blur-[120px]" style={{ background: "radial-gradient(circle, hsl(262 83% 58% / 0.5) 0%, transparent 70%)" }} />

      <UrgentOfferBar />
      {/* Nav */}
      <header className="glass-nav sticky top-0 relative z-20 px-4 py-4 md:px-12 md:py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={eridonLogo} alt="ERIDON логотип" className="h-9 w-9 shrink-0" width={36} height={36} />
            <span className="text-lg font-bold tracking-wide text-foreground">ERIDON</span>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {/* Навигация */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  Навигация <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild><Link to="/" className="cursor-pointer">Главная</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/about" className="cursor-pointer">О нас</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/contacts" className="cursor-pointer">Контакты</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/feedback" className="cursor-pointer">Отзывы</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Продукт */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  Продукт <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild><Link to="/pricing" onClick={() => reachGoal('pricing_click')} className="cursor-pointer">Тарифы</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/api" className="cursor-pointer">API для разработчиков</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/calculator" className="cursor-pointer">Калькулятор штрафов</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/docs" className="cursor-pointer">Документация API</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Документы */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  Документы <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild><Link to="/offer" className="cursor-pointer">Оферта</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/terms" className="cursor-pointer">Публичная оферта</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/privacy" className="cursor-pointer">Политика обработки ПДн</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/data-policy" className="cursor-pointer">Политика данных и токенов</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/penalty-guide" className="cursor-pointer">Гайд по штрафам ФАС</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/garantii" className="cursor-pointer">Гарантии</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/zakony" className="cursor-pointer">Законы и штрафы</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/changelog-zakona" className="cursor-pointer">Изменения закона</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/slovar" className="cursor-pointer">Словарь терминов</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Решения */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1">
                  Решения <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild><Link to="/buhgalteru" className="cursor-pointer">Бухгалтеру</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/yuristu" className="cursor-pointer">Юристу</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/agentstvu" className="cursor-pointer">Агентству</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/blogeru" className="cursor-pointer">Блогеру</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/partners" className="cursor-pointer">Партнёрам</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>


            {/* New Landing */}
            <Link to="/eridon-new" className="relative inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Новый лендинг
              <span className="inline-flex items-center rounded-full bg-gradient-to-r from-primary to-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground">
                new
              </span>
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2">
              <MaxChannelButton variant="inline" />
            </div>
            <Button size="sm" className="text-xs sm:text-sm min-h-[44px]" asChild><Link to="/auth/login" onClick={() => reachGoal('login_click')}>Войти</Link></Button>
            {/* Mobile hamburger trigger */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Открыть меню"
                  className="md:hidden inline-flex h-12 w-12 items-center justify-center rounded-lg border border-border/60 bg-card/40 text-foreground hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[85vw] max-w-sm p-0 flex flex-col bg-card/95 backdrop-blur-xl border-l border-border"
              >
                {/* Drawer header */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                  <img src={eridonLogo} alt="ERIDON" className="h-8 w-8" width={32} height={32} />
                  <span className="text-base font-bold tracking-wide text-foreground">ERIDON</span>
                </div>

                <ScrollArea className="flex-1">
                  <div className="px-3 py-4 space-y-6">
                    {/* Группа: Навигация */}
                    <div>
                      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Навигация
                      </div>
                      <nav className="flex flex-col gap-1">
                        <Link
                          to="/"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <Home className="h-4 w-4" /> Главная
                        </Link>
                        <Link
                          to="/eridon-new"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/eridon-new") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <Sparkles className="h-4 w-4 text-primary" /> Новый лендинг
                          <span className="ml-auto inline-flex items-center rounded-full bg-gradient-to-r from-primary to-accent px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground">
                            new
                          </span>
                        </Link>
                        <Link
                          to="/about"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/about") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <Info className="h-4 w-4" /> О нас
                        </Link>
                        <Link
                          to="/contacts"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/contacts") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <Mail className="h-4 w-4" /> Контакты
                        </Link>
                        <Link
                          to="/feedback"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/feedback") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <MessageSquare className="h-4 w-4" /> Отзывы
                        </Link>
                      </nav>
                    </div>

                    {/* Группа: Продукт */}
                    <div>
                      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Продукт
                      </div>
                      <nav className="flex flex-col gap-1">
                        <Link
                          to="/pricing"
                          onClick={() => { reachGoal('pricing_click'); setMobileMenuOpen(false); }}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/pricing") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <CreditCard className="h-4 w-4" /> Тарифы
                        </Link>
                        <Link
                          to="/api"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/api") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <FileText className="h-4 w-4" /> API для разработчиков
                        </Link>
                        <Link
                          to="/docs"
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${
                            isActiveRoute("/docs") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <BookOpen className="h-4 w-4" /> Документация API
                        </Link>
                      </nav>
                    </div>

                    {/* Группа: Документы */}
                    <div>
                      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Документы
                      </div>
                      <nav className="flex flex-col gap-1">
                        <Link to="/offer" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/offer") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Оферта</Link>
                        <Link to="/terms" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/terms") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Публичная оферта</Link>
                        <Link to="/privacy" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/privacy") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Политика обработки ПДн</Link>
                        <Link to="/data-policy" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/data-policy") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Политика данных и токенов</Link>
                        <Link to="/penalty-guide" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/penalty-guide") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Гайд по штрафам ФАС</Link>
                        <Link to="/garantii" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/garantii") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Гарантии</Link>
                        <Link to="/zakony" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/zakony") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Законы и штрафы</Link>
                        <Link to="/changelog-zakona" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/changelog-zakona") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Изменения закона</Link>
                        <Link to="/slovar" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/slovar") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><FileText className="h-4 w-4" /> Словарь терминов</Link>
                      </nav>
                    </div>

                    {/* Группа: Решения */}
                    <div>
                      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Решения
                      </div>
                      <nav className="flex flex-col gap-1">
                        <Link to="/buhgalteru" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/buhgalteru") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><Target className="h-4 w-4" /> Бухгалтеру</Link>
                        <Link to="/yuristu" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/yuristu") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><Target className="h-4 w-4" /> Юристу</Link>
                        <Link to="/agentstvu" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/agentstvu") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><Target className="h-4 w-4" /> Агентству</Link>
                        <Link to="/blogeru" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/blogeru") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><Target className="h-4 w-4" /> Блогеру</Link>
                        <Link to="/partners" onClick={() => setMobileMenuOpen(false)} className={`flex items-center gap-3 rounded-lg px-3 min-h-[48px] text-sm font-medium transition-colors ${isActiveRoute("/partners") ? "bg-primary/10 text-primary" : "text-foreground hover:bg-accent"}`}><Target className="h-4 w-4" /> Партнёрам</Link>
                      </nav>
                    </div>


                    {/* Группа: Аккаунт */}
                    <div>
                      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                        Аккаунт
                      </div>
                      <div className="flex flex-col gap-2 px-1">
                        <Button asChild className="w-full min-h-[48px] justify-start gap-3" onClick={() => { reachGoal('login_click'); setMobileMenuOpen(false); }}>
                          <Link to="/auth/login"><LogIn className="h-4 w-4" /> Войти</Link>
                        </Button>
                        <div onClick={() => setMobileMenuOpen(false)}>
                          <MaxChannelButton variant="inline" />
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollArea>

                {/* Drawer footer */}
                <div className="border-t border-border px-5 py-3 flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
                  <Link to="/privacy" onClick={() => setMobileMenuOpen(false)} className="hover:text-foreground transition-colors">Политика</Link>
                  <span>·</span>
                  <Link to="/terms" onClick={() => setMobileMenuOpen(false)} className="hover:text-foreground transition-colors">Условия</Link>
                  <span>·</span>
                  <Link to="/offer" onClick={() => setMobileMenuOpen(false)} className="hover:text-foreground transition-colors">Оферта</Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero — Structured Technical (B2B SaaS split-screen) */}
      <main className="bg-tech-grid relative z-10 flex flex-1 flex-col items-center px-4 sm:px-6">
        <div className="mt-10 w-full max-w-7xl md:mt-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            {/* Left: content */}
            <div className="space-y-8 animate-fade-in" style={{ animationDuration: '0.6s' }}>
              <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-card/60 px-3 py-1 text-xs font-medium uppercase tracking-wider text-accent">
                <span className="h-2 w-2 rounded-full bg-accent" />
                Соответствие ФЗ-347 и ФЗ-168
              </div>
              <div className="space-y-6">
                <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                  Маркировка рекламы <span className="text-gradient-tech">за 2 минуты.</span> Без штрафов.
                </h1>
                <p className="max-w-xl text-lg leading-relaxed text-muted-foreground">
                  Получите ERID, проверьте креатив на ФЗ-168 и сдайте отчёт в ЕРИР. Автоматически.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button asChild variant="gradient" size="lg" className="h-12 px-8 text-base">
                  <Link to="/auth?mode=signup" onClick={() => reachGoal('cta_primary_scan')}>Попробовать бесплатно</Link>
                </Button>
                <Button asChild variant="ghost" size="lg" className="h-12 rounded-xl border border-foreground/10 px-8 text-base">
                  <Link to="/demo" onClick={() => reachGoal('cta_demo')}>Демо</Link>
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span>Работает с</span>
                <span className="glass-tech px-3 py-1 font-medium text-foreground">VK ОРД</span>
                <span className="glass-tech px-3 py-1 font-medium text-foreground">Ozon ОРД</span>
              </div>
              <ol className="grid gap-3 sm:grid-cols-3">
                {["Загрузите креатив", "Получите ERID", "Забудьте об отчётности"].map((t, i) => (
                  <li key={t} className="glass-tech flex items-center gap-3 px-4 py-3 text-sm text-foreground">
                    <span className="font-mono text-accent">0{i + 1}</span>{t}
                  </li>
                ))}
              </ol>
              <div className="inline-flex max-w-xl items-center gap-2 rounded-2xl border border-accent/40 bg-card/60 px-4 py-3">
                <Sparkles className="h-5 w-5 shrink-0 text-accent" />
                <p className="text-sm font-bold leading-tight text-foreground">{PROMO_HEADLINE}</p>
              </div>
            </div>

            {/* Right: interactive dashboard mockup */}
            <HeroEridMockup />
          </div>

          {/* Scanner — moved below hero */}
          <div id="hero-scanner" className="mt-16">
            <HeroAiTextScanner />
          </div>

          {/* Role Cards */}
          <div className="mt-10">
            <RoleCards active={persona} onChange={setPersona} />
          </div>

          {/* Highlights strip */}
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {highlights.map((h, i) => (
              <div key={i} className="glass-card flex items-center gap-3 px-4 py-3 hover-scale">
                <AliveIcon icon={h.icon} animation={h.animation} className="h-4 w-4 shrink-0 text-primary" />
                <span className="text-xs font-medium text-foreground">{h.text}</span>
              </div>
            ))}
          </div>
        </div>


        {/* Trust Bar */}
        <TrustBar />

        {/* Social proof metrics band */}
        <SocialProofMetricsBand />

        {/* Image AI Scanner — расширенный режим, фото-креативы */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="mt-20 w-full max-w-5xl"
          id="image-scanner"
        >
          <div className="mb-6 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-accent">
              <ImageIcon className="h-3 w-3" /> Расширенный режим
            </div>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Проверьте <span className="gradient-text">изображение или скриншот</span>
            </h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground md:text-base">
              Загрузите фото креатива — AI распознает текст, проверит ФЗ-168 (иностранные слова) и оценит риски.
            </p>
          </div>
          <Suspense fallback={<div className="glass-card animate-pulse" style={{ minHeight: 420 }} />}>
            <HeroSandbox persona={persona} onPersonaChange={setPersona} />
          </Suspense>
        </motion.section>

        {/* Risk Assessment Quiz — depth-of-view driver */}
        <Suspense fallback={<div className="mt-20 w-full max-w-3xl glass-card animate-pulse" style={{ minHeight: 360 }} />}>
          <RiskQuiz />
        </Suspense>


        {/* ORD Partners — accredited operators */}
        <OrdPartnersStrip />

        {/* Stats counter — social proof */}
        <StatsCounter />

        {/* How it works — продукт до регистрации */}
        <HowItWorks />

        {/* Comparison vs competitors */}
        <ComparisonTable />

        {/* Cases */}
        <CasesGrid limit={3} />

        {/* Guarantee block */}
        <GuaranteeBlock />

        {/* Free guest marking sandbox */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 w-full max-w-5xl"
          id="try-mark"
        >
          <Suspense fallback={<div className="glass-card animate-pulse" style={{ minHeight: 360 }} />}>
            <LandingMarkSandbox />
          </Suspense>
        </motion.div>

        {/* Penalty Risk Calculator */}
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mt-20 w-full max-w-3xl" id="features">
          <Suspense fallback={<div className="rounded-3xl border border-border/50 p-8 animate-pulse" style={{ minHeight: 400, background: "linear-gradient(145deg, hsl(222 41% 12% / 0.9), hsl(222 47% 8% / 0.95))" }} />}>
            <PenaltyRiskCalculator />
          </Suspense>
        </motion.div>

        {/* Audience segmentation tabs */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 w-full"
          id="audiences"
        >
          <AudienceTabs />
        </motion.div>

        {/* Testimonials */}
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mt-20 w-full max-w-4xl">
          <div className="flex items-center justify-center gap-2 mb-8">
            <MessageSquareQuote className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold text-foreground">Отзывы клиентов</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="glass-card p-5 text-left animate-fade-in"
                style={{ animationDelay: `${0.1 * i}s`, animationFillMode: 'backwards' }}
              >
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, si) => (
                    <Star key={si} className={`h-3.5 w-3.5 ${si < t.rating ? "text-warning fill-warning" : "text-muted-foreground/50"}`} />
                  ))}
                </div>
                <p className="text-sm text-foreground/90 mb-4 leading-relaxed">«{t.text}»</p>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* AI Live Activity */}
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mt-20 flex justify-center">
          <Suspense fallback={<AiLiveActivitySkeleton />}>
            <AiLiveActivityLazy />
          </Suspense>
        </motion.div>

        {/* Extended FAQ */}
        <FaqExtended />

        {/* Read more — internal links to boost session depth */}
        <RelatedPagesGrid />

        {/* Social proof — now in TrustBar above */}

        {/* Contacts */}
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="w-full max-w-3xl mb-16" id="contacts">
          <h2 className="text-xl font-bold text-foreground text-center mb-6">Контакты</h2>
          <div className="glass-card p-8">
            <div className="grid gap-8 sm:grid-cols-2">
              {/* Contact info */}
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Телефон</p>
                    <a href="tel:+79015782213" className="text-sm font-medium text-foreground hover:text-primary transition-colors">+7 (901) 578-22-13</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Электронная почта</p>
                    <a href="mailto:eridonai@eridonai.online" className="text-sm font-medium text-foreground hover:text-primary transition-colors">eridonai@eridonai.online</a>
                  </div>
                </div>
              </div>

              {/* Contact form */}
              <form onSubmit={handleContactSubmit} className="space-y-3">
                <p className="text-sm font-semibold text-foreground mb-1">Напишите нам</p>
                <Input
                  placeholder="Ваше имя"
                  value={contactName}
                  onChange={e => setContactName(e.target.value)}
                  maxLength={100}
                  required
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={contactEmail}
                  onChange={e => setContactEmail(e.target.value)}
                  maxLength={255}
                  required
                />
                <Textarea
                  placeholder="Ваше сообщение..."
                  value={contactMessage}
                  onChange={e => setContactMessage(e.target.value)}
                  maxLength={1000}
                  rows={3}
                  required
                  className="resize-none"
                />
                <Button type="submit" className="w-full gap-2" disabled={contactSending}>
                  {contactSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {contactSending ? "Отправка…" : "Отправить сообщение"}
                </Button>
              </form>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Юридический футер с реквизитами ИП */}
      <SiteFooter />
      <div className="h-20 md:hidden" aria-hidden="true" />
      <MobileEridBar />

      <MaxChannelButton variant="fab" />
      <BookDemoFab onClick={() => { reachGoal('cta_demo_agency'); setDemoOpen(true); }} />
      <DemoRequestDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
